import React, { useEffect, useState } from 'react'
import styled from '@emotion/styled'
import { rgba } from 'polished'
import * as util from 'src/styles/util'
import { colors, typography, animations } from 'src/styles'
import { buttonThemes as themes } from 'src/styles/themes'
import MaterialIcon from 'src/components/MaterialIcon'
import Spinner from 'react-spinner-material'
import { MdCheck, MdClose } from 'react-icons/md'
import { baseBorderRadius, uiElementSizes } from 'src/styles/globals'
import Link from 'src/components/Link'
import { keyframes } from '@emotion/react'
import cx from 'classnames'

import { interpolate } from 'flubber'
import { motion, useMotionValue, animate, useTransform } from 'motion/react'

const useFlubber = (progress, paths) => {
  return useTransform(
    progress,
    paths.map((_, i) => i), // [0, 1, 2, ...]
    paths,
    {
      mixer: (a, b) => interpolate(a, b, { maxSegmentLength: 0.1 }),
    }
  )
}

// export const morphOffset = keyframes`
// 	0%, 100% {
//   }
//   50% {
//     transform: scaleX(1.15) scaleY(1.21);
//   }
// `

// const ButtonBackgrounds = styled.svg`
// 	${ ({ disabled }) => !disabled ? `
// 		animation: ${ morphOffset } ${ ({ duration }) => duration }ms cubic-bezier(0.370, 0.005, 0.600, 1.005) infinite;
// 	` : '' }
// `

const ButtonBackgrounds = styled.svg`
`

const buttonSettings = {
	radius: baseBorderRadius,
	border: 'none',
	transitionSpeed: animations.mediumSpeed,
	verticalOffset: '1px'
}

const animationDurations = [3200, 3000, 2800]

const getState = (loading, error, success, disabled) => {
	let buttonState = ''
	if (error) {
		buttonState = 'error'
	} else if (loading === 'true') {
		buttonState = 'loading'
	} else if (success) {
		buttonState = 'success'
	} else if (disabled) {
		buttonState = 'disabled'
	}

	return buttonState
}

const ButtonBgShape = styled.div`
	position: absolute;
	z-index: 1;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	transition: transform ${ buttonSettings.transitionSpeed } ease-in-out;
	svg {
		overflow: visible;
		width: 100%;
		height: 100%;
		transition: filter ${ buttonSettings.transitionSpeed } ease-in-out;
		path {
			transition: fill ${ buttonSettings.transitionSpeed } ease-in-out, d 100ms ease-in-out;
		}
	}
`

const setButtonTheme = (theme, state) => `
	color: ${ themes[theme].color };
	background: transparent;
	${ ButtonBgShape } {
		svg path {
			fill: ${ themes[theme].background };
		}
	}
	${ theme.shadow ? `
		box-shadow: ${ theme.shadow };
	` : `
		box-shadow: none;
	` }
	${ themes[theme].borderColor ? `
		border-color: ${ themes[theme].borderColor };
	` : `
		border-color: ${ themes[theme].background };
	` }
	&:hover {
		${ !state ? `
			color: ${ themes[theme].hoverColor };
			background: transparent;
			${ ButtonBgShape } {
				svg {
					path {
						// animation-play-state: running;
						fill: ${ themes[theme].hoverBackground };
					}
				}
			}
			${ themes[theme].borderHoverColor ? `
				border-color: ${ themes[theme].borderHoverColor };
			` : `
				border-color: ${ themes[theme].hoverBackground };
			` }
			${ theme.hoverShadow ? `
				box-shadow: ${ theme.hoverShadow };
			` : '' }
		` : '' }
	}
`

const DisabledButtonStyles = () => `
	&[disabled],
	&:disabled {
		opacity: .5;
		// background: ${ colors.transparent };
		// border-color: ${ colors.textColor };
		// color: ${ rgba(colors.bgColor, 0.6) };
		cursor: not-allowed;
	}
`

const ButtonIcon = styled.div`
	${ ({ position }) => position === 'left' ? `
		margin-right: .5em;
	` : `
		margin-left: .5em;
	` }
	span,
	svg {
		display: block;
	}
`

const buttonPaddingIncrement = {
	normal: 0.666,
	wide: 0.666,
	extraWide: 0.666
}

const ButtonStyles = (state, shape, size, theme, buttonWidth) => (`
	position: relative;
	appearance: none;
	-webkit-tap-highlight-color: rgba(0,0,0,0);
	-webkit-touch-callout: none;
	outline: none;
	cursor: pointer;
	display: inline-block;
	vertical-align: middle;
	border: ${ buttonSettings.border };
	height: ${ uiElementSizes[size] };
	${ util.responsiveStyles('height', uiElementSizes[size] * 1.1, uiElementSizes[size], uiElementSizes[size], uiElementSizes[size]) }
	padding: 0 calc(${ uiElementSizes[size] }px * ${ buttonPaddingIncrement[buttonWidth] }) ${ buttonSettings.verticalOffset };
	min-width: calc(${ uiElementSizes[size] }px * 2.5);
	text-transform: none;
	letter-spacing: 0;
	// border-radius: ${ buttonSettings.radius }px;
	border-radius: 50%;
	font-size: 14px;
	text-align: center;
	${ typography.buttonStyle }
	line-height: 1em;
	${ util.fontSmoothing }
	transition: background ${ buttonSettings.transitionSpeed } ease-in-out,
							color ${ buttonSettings.transitionSpeed } ease-in-out,
							border ${ buttonSettings.transitionSpeed } ease-in-out,
							box-shadow ${ buttonSettings.transitionSpeed } ease-in-out,
							transform ${ buttonSettings.transitionSpeed } ease-in-out,
							opacity ${ buttonSettings.transitionSpeed } ease-in-out;
	// Button States
	${ state === 'loading' ? 'cursor: wait; pointer-events: none;' : '' }
	${ state === 'error' || state === 'success' ? 'cursor: default; pointer-events: none;' : '' }

	// Button Shapes
	${ shape ? `
		${ shape.includes('circle') || shape.includes('square') ? `
			padding: 0 !important;
			${ util.responsiveStyles('width', uiElementSizes[size] * 1.1, uiElementSizes[size], uiElementSizes[size], uiElementSizes[size]) }
			${ util.responsiveStyles('min-width', uiElementSizes[size] * 1.1, uiElementSizes[size], uiElementSizes[size], uiElementSizes[size]) }
			${ ButtonIcon } {
				margin: 0;
			}
		` : '' }
	` : '' }
	${ shape && shape.includes('circle') ? 'border-radius: 50%;' : '' }

	// Button Themes
	${ setButtonTheme(theme, state) }
	${ state === 'disabled' ? `${ DisabledButtonStyles() }` : '' }

	// Button Size Tweaks
	${ size === 'small' ? `
		font-size: inherit;
	` : '' }
	${ size === 'tiny' ? `
		font-size: inherit;
		padding: 0 calc(${ uiElementSizes[size] }px * .75) ${ buttonSettings.verticalOffset };
	` : '' }

`)

const ButtonContent = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	height: 100%;
	width: 100%;
	position: relative;
	z-index: 2;
	svg {
		* {
			fill: currentcolor;
		}
	}
`

const StyledButtonLink = styled(Link)`
	${ ({ loading, error, success, disabled, shape, size, theme, buttonWidth }) => ButtonStyles(getState(loading, error, success, disabled), shape, size, theme, buttonWidth) }
`

const StyledButtonElement = styled.div`
	${ ({ loading, error, success, disabled, shape, size, theme, buttonWidth }) => ButtonStyles(getState(loading, error, success, disabled), shape, size, theme, buttonWidth) }
`

const shapes = [
	'M153.16,15.72C112.71,2.58,63.64-0.97,22.92,0.32C4.64,0.89-0.43,16.71,1.84,34.54s5.7,28.73,22.22,36.93 s76.22,8.38,124.92,3.27c11.78-1.24,30.17-4.47,27.27-29.42C174.8,32.88,172.45,21.99,153.16,15.72z',
	'M176.58,44.99c1.31-12.59,3.55-25.33-23.64-32.33C135.23,8.1,54.49-2.76,21.61,1.91 C3.81,4.43-2.24,17.84,1.85,39.47C5.27,57.6,8.27,70.13,33.23,73.53c35.5,4.84,82.79,5.44,119.54,0.65 C170.27,71.91,174.91,60.99,176.58,44.99z',
	'M153.81,73.71c12.49-2.06,23.41-10.5,23.09-28.46c-0.32-18.28-1.31-36.04-34.66-42.7 C124.63-0.98,64.39-1.16,28.47,9.66C10.8,14.98-1.08,23.32,1.27,42.33C3.58,60.99,7.27,73.08,40.25,76.1 C67.82,78.63,122.31,78.9,153.81,73.71z',
	'M38.89,76.23c24.22,1.3,73.74,2.22,108.86-2.56c24.02-3.27,28.11-9.85,29.36-30.96 C178.17,24.77,173.78,8.05,148,3.6C117.05-1.73,43.26,2.01,24.62,6.44c-14,3.33-23.98,5.58-23.98,38.7 C0.64,61.16,7.01,74.52,38.89,76.23z',
	'M0.61,36.13c0,17.87,2.95,38.42,38.32,40.79c29.02,1.95,71.09,1.95,107.01-1.24 c17.9-1.59,31.35-7.12,31.35-29.8c0-31.41-7.67-35.69-26.64-38.43C132.04,4.75,55.25-2.31,27.66,0.76 C13.46,2.34,0.61,4.2,0.61,36.13z',
	'M24.21,2.7C7.56,3.54-1.32,11.99,0.16,38.72c1.56,28.13,5.85,33.05,41.81,35.71 c24.04,1.78,83.72,3.42,106.26,0.89c22.39-2.51,28.08-12.45,29.48-32.22c1.32-18.76-4.02-28.08-31.02-34.54 C111.79,0.21,56.09,1.07,24.21,2.7z',
	// <ButtonShape7 key='shape7' />
]

const Button = ({
	to,
	external,
	target,
	icon,
	iconPosition = 'left',
	loading,
	error,
	success,
	disabled,
	onClick,
	setTheme = 'default',
	className,
	shape = 'default',
	size = 'medium',
	title,
	name,
	stagger = 0,
	label,
	as,
	children
}) => {
	const [shapeIndex, setShapeIndex] = useState(stagger || 0)
	const progress = useMotionValue(0)
	// const currentShape = useFlubber(progress, shapes)
	const currentShape = shapes[shapeIndex]

	useEffect(() => {
		const controls = animate(progress, shapes.length - 1, {
			duration: shapes.length * 2, // scale duration with # of shapes
			ease: 'easeInOut',
			repeat: Infinity,
			repeatType: 'loop',
		})

		return () => controls.stop()
	}, [progress])

	const renderIcon = (icon, position, shape, size) => {
		let renderedIcon = false
		if (typeof icon === 'string') {
			renderedIcon = <ButtonIcon size={size} position={position} shape={shape}><MaterialIcon size={size === 'tiny' ? '18px' : '24px'}>{icon}</MaterialIcon></ButtonIcon>
		} else {
			renderedIcon = <ButtonIcon size={size} position={position} shape={shape}>{icon}</ButtonIcon>
		}
		return renderedIcon
	}

	const renderButtonContent = () => {
		if (loading) {
			return <ButtonContent>
				<Spinner radius={18} color='inherit' stroke={2} />
			</ButtonContent>
		} else if (error) {
			return <ButtonContent>
				<MdClose size={'1.5em'} />
			</ButtonContent>
		} else if (success) {
			return <ButtonContent>
				<MdCheck size={'1.5em'} />
			</ButtonContent>
		} else {
			return <ButtonContent>
				{icon && iconPosition !== 'right' ? renderIcon(icon, iconPosition, shape, size) : false}
				{children || label}
				{icon && iconPosition === 'right' ? renderIcon(icon, iconPosition, shape, size) : false}
			</ButtonContent>
		}
	}

	const renderButtonBackground = (timeout, disabled) => {
		return (
			<ButtonBackgrounds viewBox="0 0 177.89 78.25" preserveAspectRatio="none">
				<motion.path
					d={currentShape}
					// duration={0.8}
					// ease="easeInOut"
				/>
				{/* {!disabled ? shapes[shapeIndex] : shapes[1]} */}
			</ButtonBackgrounds>
		)
	}

	let buttonWidth = 'normal'
	if (children?.length > 14) {
		buttonWidth = 'extraWide'
	} else if (children?.length > 10) {
		buttonWidth = 'wide'
	}

	if (to) {
		return (
			<StyledButtonLink
				className={cx('button', className)}
				to={to}
				target={target}
				external={external}
				icon={icon}
				iconPosition={iconPosition}
				loading={loading ? loading.toString() : 'false'}
				error={error}
				success={success}
				disabled={disabled}
				onClick={onClick}
				theme={setTheme || 'default'}
				shape={shape}
				size={size}
				title={title}
				name={name || title}
				aria-label={name || title}
				rel={external ? 'noopener noreferrer' : ''}
				buttonWidth={buttonWidth}
			>
				{setTheme !== 'transparent' && (
					<ButtonBgShape>
						{renderButtonBackground(animationDurations[stagger], disabled)}
					</ButtonBgShape>
				)}
				{renderButtonContent()}
			</StyledButtonLink>
		)
	} else {
		return (
			<StyledButtonElement
				className={cx('button', className)}
				icon={icon}
				iconPosition={iconPosition}
				loading={loading ? loading.toString() : 'false'}
				error={error}
				success={success}
				disabled={disabled}
				onClick={onClick}
				theme={setTheme || 'default'}
				shape={shape}
				size={size}
				title={title}
				name={name || title}
				aria-label={name || title}
				buttonWidth={buttonWidth}
				as={as || 'button'}
			>
				{setTheme !== 'transparent' && (
					<ButtonBgShape>
						{renderButtonBackground(animationDurations[stagger], disabled)}
					</ButtonBgShape>
				)}
				{renderButtonContent()}
			</StyledButtonElement>
		)
	}
}

export default Button
